import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { apiRequest, clearCsrf } from '@/api/client'
import type { CurrentUser } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const initialized = ref(false)
  const isAuthenticated = computed(() => user.value !== null)

  async function restore(): Promise<void> {
    if (initialized.value) return
    try {
      user.value = await apiRequest<CurrentUser>('/auth/me')
    } catch {
      user.value = null
    } finally {
      initialized.value = true
    }
  }

  async function login(username: string, password: string): Promise<CurrentUser> {
    clearCsrf()
    const authenticated = await apiRequest<CurrentUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    user.value = authenticated
    initialized.value = true
    return authenticated
  }

  async function logout(): Promise<void> {
    try {
      await apiRequest<void>('/auth/logout', { method: 'POST' })
    } finally {
      user.value = null
      initialized.value = true
      clearCsrf()
    }
  }

  async function changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<CurrentUser> {
    const updated = await apiRequest<CurrentUser>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    })
    user.value = updated
    return updated
  }

  function can(permission: string): boolean {
    return user.value?.permissions.includes(permission) ?? false
  }

  return { user, initialized, isAuthenticated, restore, login, logout, changePassword, can }
})
